import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { MapPin, Clock, Users } from 'lucide-react'
import { apiGet } from '../../lib/api.ts'

interface Job {
  id: number
  title: string
  job_status: 'draft' | 'published' | 'filled' | 'closed' | 'cancelled'
  employment_type: string
  location_city: string
  start_date: string
  end_date: string
  application_count: number
}

export default function MyJobsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState<string>('')

  useEffect(() => {
    void fetchJobs()
  }, [])

  useEffect(() => {
    const state = location.state as { notice?: string } | null
    if (state?.notice) {
      setNotice(state.notice)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const fetchJobs = async () => {
    try {
      const data = await apiGet<{ jobs: Job[] }>(
        '/practice/request-staff.php?format=json'
      )

      setJobs(data.jobs)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load jobs.')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'text-green-600 bg-green-50'
      case 'filled': return 'text-blue-600 bg-blue-50'
      case 'draft': return 'text-yellow-600 bg-yellow-50'
      case 'closed': return 'text-gray-600 bg-gray-50'
      case 'cancelled': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center text-red-600 bg-red-50 p-4 rounded-lg">
          {error}
        </div>
        <Button onClick={fetchJobs} className="w-full mt-4">
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-900">My Jobs</h1>
        <Button
          className="bg-brand-600 hover:bg-brand-700 text-white"
          onClick={() => navigate('/practice/jobs/new')}
        >
          Post New Job
        </Button>
      </div>

      {notice && <div className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{notice}</div>}

      {jobs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-text-700">No jobs posted yet.</p>
          <p className="text-sm text-text-500 mt-2">Create your first job posting to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <button
              key={job.id}
              type="button"
              className="block w-full text-left"
              onClick={() => navigate(`/practice/jobs/${job.id}`)}
            >
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-brand-700">{job.title}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <MapPin size={16} />
                      {job.location_city}
                    </CardDescription>
                    <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-text-500">
                      {(job.employment_type || 'job').replace('_', ' ')}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(job.job_status)}`}>
                    {job.job_status}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-text-600">
                      <Clock size={16} />
                      {new Date(job.start_date).toLocaleDateString()} - {new Date(job.end_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1 text-text-600">
                      <Users size={16} />
                      {job.application_count} candidate{job.application_count === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
